<template>
  <v-stepper-window-item value="2">
    <v-card-text>

      <v-row v-if="groupedErrors.length">
        <v-col cols="12">
          <v-alert
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Validation failed with {{ totalErrorCount }} error{{ totalErrorCount !== 1 ? 's' : '' }}
          </v-alert>

          <v-expansion-panels variant="accordion">
            <v-expansion-panel
              v-for="group in groupedErrors"
              :key="group.key"
            >
              <v-expansion-panel-title class="error-group-title">
                <v-icon size="small" color="error" class="mr-3">{{ group.icon }}</v-icon>
                <span class="text-body-2 font-weight-medium">{{ group.label }}</span>
                <v-chip size="x-small" color="error" variant="tonal" class="ml-3">
                  {{ group.errors.length }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text class="error-panel-content">
                <v-alert
                  v-if="group.hint"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="ma-3 mb-0"
                >
                  <template v-if="Array.isArray(group.hint)">
                    <p v-for="(paragraph, pIdx) in group.hint" :key="pIdx" :class="{ 'mb-2': pIdx < group.hint.length - 1, 'mb-0': pIdx === group.hint.length - 1 }">
                      {{ paragraph }}
                    </p>
                  </template>
                  <template v-else>{{ group.hint }}</template>
                </v-alert>
                <v-text-field
                  v-model="errorSearch[group.key]"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Filter errors..."
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  class="ma-3 mb-0"
                />
                <v-data-table
                  :headers="errorTableHeaders"
                  :items="group.errors"
                  :items-per-page="10"
                  :items-per-page-options="[10, 25, 50]"
                  :search="errorSearch[group.key]"
                  :custom-filter="errorFilter"
                  density="compact"
                  class="error-table"
                >
                  <template v-slot:[`item.row`]="{ item }">
                    <span class="text-medium-emphasis">{{ item.row ?? '-' }}</span>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="8">
          <v-file-input
            label="Upload File"
            accept=".xlsx,.xml"
            @update:model-value="handleFileUpload"
            :hint="fileUploadHint"
            :persistent-hint="true"
          ></v-file-input>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="taskKey"
            label="Key (optional)"
            hint="Lock key stamped on each imported task and presented with the commit"
            :persistent-hint="true"
            clearable
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Calendar Selection: shown once a file is chosen for schedules that
           still need a calendar-per-time-range mapping. For P6 XML files the
           mapping is pre-resolved by deterministic code match when possible. -->
      <v-row v-if="selectedFile && mappingRequired">
        <v-col cols="12">
          <v-card
            title="Calendar Selection"
            subtitle="Assign a calendar to each time range"
            variant="elevated"
            elevation="3"
          >
            <v-card-text>
              <v-alert
                v-if="publishedCalendars.length === 0"
                type="error"
                class="mb-4"
              >
                No published calendars available. A calendar is required for scheduling dates.
              </v-alert>

              <v-alert
                v-else-if="publishedCalendars.length === 1"
                type="info"
                variant="tonal"
                class="mb-4"
              >
                Calendar: <strong>{{ publishedCalendars[0].title }}</strong> will be applied to all time ranges
              </v-alert>

              <template v-else>
                <v-alert
                  v-if="autoMatchedCalendar"
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="mb-2"
                >
                  Matched calendar <strong>{{ autoMatchedCalendar.title }}</strong> from the P6 file by code ({{ autoMatchedCalendar.code }}). Adjust below if needed.
                </v-alert>
                <v-alert
                  v-else-if="isXmlFile"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-2"
                >
                  The P6 file's calendars don't match any published calendar yet. Select a calendar for each time range.
                </v-alert>
                <v-select
                  v-for="trt in timeRangeTemplates"
                  :key="trt.id"
                  v-model="calendarSelections[trt.id]"
                  :items="calendarDropdownItems"
                  item-title="label"
                  item-value="id"
                  :label="`Calendar for ${trt.title}`"
                  variant="outlined"
                  density="comfortable"
                  class="mb-2"
                />
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-card-text>

    <ProcessingProgressDialog
      :model-value="processing"
      :stage="progressStage"
      :done="progressDone"
      :total="progressTotal"
    />

    <v-card-actions>
      <v-btn @click="$emit('back')">
        Back
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :disabled="!isValid"
        @click="handleContinue"
      >
        Continue
      </v-btn>
    </v-card-actions>
  </v-stepper-window-item>
</template>

<script setup>
import { ref, reactive, computed, shallowRef, watch, nextTick } from 'vue';
import { useTaskStore } from '@/stores/task';
import { useScheduleStore } from '@/stores/schedule';
import { SpreadsheetProcessor } from '@/utils/SpreadsheetProcessor';
import { extractP6CalendarSummary, matchP6CalendarByCode, parseP6XmlDocument } from '@/utils/XMLTaskParser';
import { yieldToPaint } from '@/utils/mainThreadScheduler';
import ProcessingProgressDialog from '@/components/dialogs/ProcessingProgressDialog.vue';

const taskStore = useTaskStore();
const scheduleStore = useScheduleStore();

const props = defineProps({
  nestedTasks: {
    type: Array,
    required: true
  },
  selectedSchedule: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['update:nested-tasks', 'update:task-sets', 'update:selected-schedule', 'update:task-key', 'back', 'continue']);

const tasks = computed(() => taskStore.getTasks);
const nestedTasksRef = ref([]);
const isValid = ref(false);
const validationErrors = ref([]);
const groupedErrors = shallowRef([]);
const selectedTimeRangeTemplate = ref(null);

// Optional lock key for the uploaded tasks: handed to the parent on Continue,
// which stamps it on every task at commit time and presents it in the
// commit's `keys` list so tasks locked by a previous keyed import may be
// updated. Commit-time stamping keeps the field free to change without
// re-processing the uploaded file.
const taskKey = ref('');

// --- Calendar-per-time-range mapping (shown once a file is chosen) ---
const selectedFile = ref(null);
const calendarSelections = ref({});
// Published calendar auto-matched from a P6 file's default calendar by
// deterministic code; null for Excel uploads or when no code matches.
const autoMatchedCalendar = ref(null);
// selectedSchedule with the built timeRangeCalendars attached. Kept local so
// the mapping card stays visible for adjustments; pushed to the parent on
// Continue.
const enrichedSchedule = ref(null);

const isXmlFile = computed(() =>
  !!selectedFile.value && selectedFile.value.name.toLowerCase().endsWith('.xml')
);

const publishedCalendars = computed(() => scheduleStore.getCalendars || []);

const calendarDropdownItems = computed(() =>
  publishedCalendars.value.map(c => ({ ...c, label: `${c.title} (${c.code})` }))
);

const timeRangeTemplates = computed(() => {
  const template = scheduleStore.getScheduleTemplatesObj[props.selectedSchedule?.scheduleTemplateId];
  return template?.[0]?.timeRangeTemplates || [];
});

const usesDates = computed(() => {
  const schedule = props.selectedSchedule;
  if (!schedule) return false;
  if (schedule.timeRanges && schedule.timeRanges.length > 0) {
    return !schedule.timeRanges.every(range => range.type === 'none');
  }
  const templates = timeRangeTemplates.value;
  return templates.length > 0 && !templates.every(range => range.type === 'none');
});

// The schedule still lacks a (complete) calendar-per-time-range mapping.
const needsCalendarSelection = computed(() => {
  const existing = props.selectedSchedule?.timeRangeCalendars;
  if (!existing || existing.length === 0) return true;
  return timeRangeTemplates.value.some(trt =>
    !existing.find(trc => trc.timeRangeTemplateId === trt.id && trc.calendarId)
  );
});

const mappingRequired = computed(() => usesDates.value && needsCalendarSelection.value);

const mappingComplete = computed(() => {
  if (!mappingRequired.value) return true;
  if (publishedCalendars.value.length === 0) return false;
  if (publishedCalendars.value.length === 1) return true;
  return timeRangeTemplates.value.every(trt => calendarSelections.value[trt.id]);
});

function buildTimeRangeCalendars() {
  return timeRangeTemplates.value
    .map(trt => {
      const calendarId = publishedCalendars.value.length === 1
        ? publishedCalendars.value[0].id
        : calendarSelections.value[trt.id];
      const calendar = publishedCalendars.value.find(c => c.id === calendarId);
      return {
        timeRangeTemplateId: trt.id,
        calendarId: calendar?.id || null,
        calendarVersionReference: calendar?.versionReference || null
      };
    })
    .filter(entry => entry.calendarId && entry.calendarVersionReference);
}

const totalErrorCount = computed(() =>
  groupedErrors.value.reduce((sum, group) => sum + group.errors.length, 0)
);

const errorSearch = reactive({});

const errorTableHeaders = [
  { title: 'Row', key: 'row', width: '80px' },
  { title: 'Details', key: 'message', sortable: false },
];

const errorFilter = (value, query, item) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const row = item.raw.row != null ? String(item.raw.row) : '';
  return row.includes(q) || item.raw.message.toLowerCase().includes(q);
};

const fileUploadHint = computed(() => {
  if (groupedErrors.value.length) {
    return 'Validation errors detected. Please check and reupload.';
  }
  if (selectedFile.value && mappingRequired.value && !mappingComplete.value) {
    return 'Assign a calendar to each time range to validate the file';
  }
  return nestedTasksRef.value.length ? 'File uploaded successfully' : 'Please upload an Excel or XML file';
});

// Progress dialog state, fed by SpreadsheetProcessor's onProgress events.
const processing = ref(false);
const progressStage = ref('');
const progressDone = ref(null);
const progressTotal = ref(null);

// Monotonic token: a re-run (new file, adjusted mapping) invalidates any
// still-running older run so its result and progress events are discarded.
let processRunId = 0;
// Effective calendar mapping of the last started run, to skip re-processing
// when a selection change resolves to the identical mapping.
let lastProcessedMappingKey = null;
let mappingWatchTimer = null;
// Parsed Document of the selected P6 file, kept from the calendar pre-scan so
// every processing run of this file (initial, and re-runs after the user
// adjusts the mapping) skips the file read and the blocking DOM parse. Plain
// variable, not a ref: a DOM tree must never be made reactive. Null for Excel
// files and when the pre-scan didn't run.
let prescannedXmlDoc = null;

function currentMappingKey() {
  return mappingRequired.value ? JSON.stringify(buildTimeRangeCalendars()) : '';
}

const handleFileUpload = async (file) => {
  selectedFile.value = file || null;
  calendarSelections.value = {};
  autoMatchedCalendar.value = null;
  enrichedSchedule.value = null;
  clearTimeout(mappingWatchTimer);
  lastProcessedMappingKey = null;
  prescannedXmlDoc = null;
  resetProcessingState();

  if (!file) return;

  // For P6 XML files, try to pre-resolve the mapping by matching the file's
  // default calendar code against the published calendars. The scan parses
  // the whole file synchronously (DOMParser), which freezes the UI on large
  // exports, so it runs behind the progress dialog: show it, yield a frame so
  // it actually paints, then scan. A match hands straight off to processing
  // (which keeps the dialog up); otherwise the dialog closes and the user
  // picks the calendars, which triggers the watcher below.
  if (file.name.toLowerCase().endsWith('.xml') && mappingRequired.value && publishedCalendars.value.length > 1) {
    const scanRunId = ++processRunId;
    processing.value = true;
    progressStage.value = 'scanning-calendars';
    progressDone.value = null;
    progressTotal.value = null;
    try {
      await nextTick();
      await yieldToPaint();
      const xmlText = await file.text();
      // A newer file was chosen while this one was being read — drop it.
      if (scanRunId !== processRunId) return;
      prescannedXmlDoc = parseP6XmlDocument(xmlText);
      const { defaultCalendar } = extractP6CalendarSummary(prescannedXmlDoc);
      const matched = matchP6CalendarByCode(defaultCalendar, publishedCalendars.value);
      if (matched) {
        autoMatchedCalendar.value = matched;
        const selections = {};
        timeRangeTemplates.value.forEach(trt => { selections[trt.id] = matched.id; });
        calendarSelections.value = selections;
        // Process directly rather than via the (debounced) watcher so the
        // dialog stays up with no gap; the watcher then sees an unchanged
        // mapping key and skips.
        await processCurrentFile();
        return;
      }
    } catch (error) {
      console.error('Error pre-scanning P6 calendars:', error);
    } finally {
      // processCurrentFile owns the flag once it has started (it bumps the
      // run id); only close the dialog if this scan is still the latest run.
      if (scanRunId === processRunId) processing.value = false;
    }
  }

  // No mapping needed (or single/no published calendar): process right away.
  // Otherwise wait for the user to complete the selections.
  if (mappingComplete.value) {
    await processCurrentFile();
  }
};

// Process (or re-process) once the mapping is filled in or adjusted. Empty
// selections mean a reset (new file), not user input — handleFileUpload owns
// processing in that case, so skip to avoid double-processing. Debounced so
// assigning several time ranges in a row costs one run, and skipped entirely
// when the effective mapping is unchanged from the last processed one.
watch(calendarSelections, (selections) => {
  if (!selectedFile.value || !mappingRequired.value) return;
  if (!Object.keys(selections).length) return;
  if (!mappingComplete.value) return;
  clearTimeout(mappingWatchTimer);
  mappingWatchTimer = setTimeout(() => {
    if (currentMappingKey() === lastProcessedMappingKey) return;
    processCurrentFile();
  }, 300);
}, { deep: true });

const processCurrentFile = async () => {
  const file = selectedFile.value;
  if (!file) return;

  let schedule = props.selectedSchedule;
  if (mappingRequired.value) {
    schedule = { ...schedule, timeRangeCalendars: buildTimeRangeCalendars() };
    enrichedSchedule.value = schedule;
  }
  lastProcessedMappingKey = currentMappingKey();

  const runId = ++processRunId;
  processing.value = true;
  progressStage.value = 'reading-file';
  progressDone.value = null;
  progressTotal.value = null;

  try {
    const processor = new SpreadsheetProcessor(schedule, selectedTimeRangeTemplate.value, tasks.value);
    const result = await processor.processFile(file, {
      xmlDoc: prescannedXmlDoc,
      onProgress: ({ stage, done, total }) => {
        if (runId !== processRunId) return;
        progressStage.value = stage;
        progressDone.value = done ?? null;
        progressTotal.value = total ?? null;
      }
    });

    // A newer run started while this one was yielding — its state owns the
    // UI now; drop this result.
    if (runId !== processRunId) return;

    // Check if the spreadsheet is valid
    if (!result.isValid) {
      isValid.value = false;
      validationErrors.value = result.validationErrors || result.errors || [];
      groupedErrors.value = result.groupedErrors || [];
      return;
    }

    emit('update:processed-tasks', {
      nestedTasks: result.nestedTasks,
      displayTasks: result.displayTasks,
      taskSetsPayload: result.taskSetsPayload,
      changes: result.changes,
      // P6-only metadata (empty for Excel uploads): used to prompt the calendar
      // import dialog and to re-link tasks to real calendar ids after import.
      p6Calendars: result.p6Calendars || [],
      taskCalendarLinks: result.taskCalendarLinks || null,
      commitKeys: result.commitKeys || [],
      relationshipSummary: result.relationshipSummary || null,
      prunedEmptyWbsCount: result.prunedEmptyWbsCount || 0,
      durationSummary: result.durationSummary || null
    });

    isValid.value = true;
    validationErrors.value = [];
    groupedErrors.value = [];

    // Log for debugging
    console.log('File processing result:', result);
  } catch (error) {
    console.error('Error processing file:', error);
    if (runId === processRunId) resetProcessingState();
  } finally {
    if (runId === processRunId) processing.value = false;
  }
};

const handleContinue = () => {
  // Hand the schedule (with the newly built calendar mapping) back to the
  // parent so later steps and the commit see the same calendar resolution the
  // file was processed with.
  if (enrichedSchedule.value) {
    emit('update:selected-schedule', enrichedSchedule.value);
  }
  emit('update:task-key', taskKey.value?.trim() || null);
  emit('continue');
};

const resetProcessingState = () => {
  nestedTasksRef.value = [];
  emit('update:nested-tasks', []);
  emit('update:task-sets', []);
  isValid.value = false;
  validationErrors.value = [];
  groupedErrors.value = [];
  Object.keys(errorSearch).forEach(k => delete errorSearch[k]);
};
</script>

<style scoped>
.error-group-title {
  min-height: 44px;
}

.error-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}

.error-table {
  font-size: 0.8125rem;
}
</style>
